import React, { useMemo } from "react";

import { AiChatTurn } from "@intelligems/ig-types/schemas/ai/ai-chat-state";
import { UiMessage } from "@intelligems/ig-types/schemas/ai/ui-message";
import { groupAndFilterNodes, isAssistantTextBubble } from "./part-processor";
import { PartBuilder, RenderPart } from "./PartBuilder";

import {
  FlattenedPart,
  MessageFlags,
} from "@src/features/ai/components/WorkflowEditor/execution/types";

type TurnProps = {
  turn: AiChatTurn;
  isLastTurn?: boolean;
  transformMessage?: (message: UiMessage) => UiMessage;
  renderPart?: RenderPart;
} & MessageFlags;

export const Turn = (props: TurnProps) => {
  const { turn, isLastTurn, transformMessage, renderPart, ...flags } = props;

  const itemsToRender = useMemo(() => {
    const flattenedParts: FlattenedPart[] = turn.nodes.flatMap((n) =>
      n.messages.flatMap((m) => {
        // Apply the consumer's view transform once, here at the single point
        // where messages are materialised — so every downstream reader (the
        // last-bubble gate and the renderer) sees the converted parts. The
        // transform returns a copy; raw chat state is never mutated. Called
        // for every role — the consumer branches on `message.role` to scope.
        const message = transformMessage ? transformMessage(m) : m;
        // eslint-disable-next-line max-nested-callbacks
        return message.parts.flatMap((p) => ({
          nodeId: n.id,
          nodeName: n.name,
          nodeType: n.type,
          messageId: message.id,
          messageRole: message.role,
          messageTimestamp: message.timestamp,
          part: p,
          type: "part" as const,
          timestamp: p.timestamp,
        }));
      })
    );

    return groupAndFilterNodes(
      flattenedParts,
      !!props.debug,
      props.showExpandedReasoningMessages,
      props.reasoningNodeAllowList,
      props.showToolsAsSteps,
      props.conversationTools
    );
  }, [
    turn.nodes,
    props.debug,
    props.showExpandedReasoningMessages,
    props.reasoningNodeAllowList,
    props.showToolsAsSteps,
    props.conversationTools,
    transformMessage,
  ]);

  const lastTextItemIndex = itemsToRender.findLastIndex(isAssistantTextBubble);

  return itemsToRender.map((item, i) => (
    <PartBuilder
      data-id={item.nodeId}
      item={item}
      index={i}
      isLast={!!isLastTurn && i === lastTextItemIndex}
      isLastTurn={isLastTurn}
      isTrailing={i === itemsToRender.length - 1}
      key={`${item.nodeId}-${i}`}
      renderPart={renderPart}
      {...flags}
    />
  ));
};
